import { Title } from '@solidjs/meta'
import { A, useParams } from '@solidjs/router'
import { type Component, For, Show } from 'solid-js'
import Button from '~/components/Button'
import Card from '~/components/Card'
import Container from '~/components/Container'
import Icon from '~/components/Icon'
import { type Path, useNavigate } from '~/router'
import { type CartLineType, cartLines, cartSubtotal, removeFromCart, setLineQuantity } from '~/stores/cart'

const money = (value: number) => `$${value.toLocaleString('es-CO')}`

/**
 * El carrito con espacio para revisarlo.
 *
 * El cajón sirve para "acabo de añadir algo y sigo mirando"; esta página es
 * para "voy a comprar de verdad": cantidades cómodas de tocar en móvil, cada
 * línea enlazada a su ficha por si hay dudas, y el total a la vista.
 */
const CarritoPage: Component = () => {
  const params = useParams()
  const navigate = useNavigate()
  const empresa = () => params.empresa ?? ''

  const lines = () => cartLines(empresa())
  const currency = () => lines()[0]?.currency ?? 'COP'

  const step = (line: CartLineType, delta: number) => setLineQuantity(empresa(), line.productId, line.variantId, line.quantity + delta)

  return (
    <>
      <Title>Tu carrito</Title>

      <Container class="py-8">
        <div class="mb-6 flex items-center justify-between gap-3">
          <h1 class="text-2xl font-bold text-foreground">Tu carrito</h1>
          <A href={`/catalogo/${empresa()}`}>
            <Button variant="text" color="default" prependIcon="material-symbols-light:arrow-back">
              Seguir comprando
            </Button>
          </A>
        </div>

        <Show
          when={lines().length}
          fallback={
            <Card>
              <Card.Body>
                <div class="flex flex-col items-center justify-center gap-4 py-14 text-center">
                  <Icon icon="material-symbols-light:shopping-cart-outline" size={52} class="text-foreground-subtle" />
                  <div>
                    <p class="text-lg font-medium text-foreground">Tu carrito está vacío</p>
                    <p class="text-sm text-foreground-subtle">Cuando añadas artículos aparecerán aquí.</p>
                  </div>
                  <A href={`/catalogo/${empresa()}`}>
                    <Button prependIcon="material-symbols-light:storefront-outline">Ver el catálogo</Button>
                  </A>
                </div>
              </Card.Body>
            </Card>
          }
        >
          <div class="grid grid-cols-1 gap-6 lg:grid-cols-3 items-start">
            {/* Líneas */}
            <div class="lg:col-span-2 space-y-3">
              <For each={lines()}>
                {line => (
                  <Card>
                    <Card.Body>
                      <div class="flex gap-4">
                        <Show when={line.image}>
                          <A href={`/catalogo/${empresa()}/${line.key}`} class="shrink-0">
                            <img src={line.image as string} alt={line.name} class="h-24 w-24 rounded-lg object-cover" />
                          </A>
                        </Show>

                        <div class="min-w-0 flex-1">
                          <A href={`/catalogo/${empresa()}/${line.key}`} class="font-medium text-foreground hover:text-primary-500">
                            {line.name}
                          </A>
                          <Show when={line.variantLabel}>
                            <p class="text-sm text-foreground-subtle">{line.variantLabel}</p>
                          </Show>
                          <p class="text-sm text-foreground-subtle">{money(line.unitPrice)} c/u</p>

                          <Show when={line.trackInventory && line.stock > 0 && line.quantity >= line.stock}>
                            <p class="mt-1 text-xs text-warning">Es todo lo que queda de este artículo.</p>
                          </Show>

                          <div class="mt-3 flex flex-wrap items-center justify-between gap-3">
                            <div class="inline-flex items-center rounded-lg border border-border overflow-hidden">
                              <Button variant="text" color="default" size="sm" icon="material-symbols-light:remove" aria-label="Quitar uno" onClick={() => step(line, -1)} />
                              <span class="w-10 text-center text-sm font-semibold text-foreground">{line.quantity}</span>
                              <Button
                                variant="text"
                                color="default"
                                size="sm"
                                icon="material-symbols-light:add"
                                aria-label="Añadir uno"
                                disabled={line.trackInventory && line.stock > 0 && line.quantity >= line.stock}
                                onClick={() => step(line, 1)}
                              />
                            </div>

                            <div class="flex items-center gap-3">
                              <span class="text-lg font-bold text-foreground">{money(line.unitPrice * line.quantity)}</span>
                              <Button
                                variant="text"
                                color="danger"
                                size="sm"
                                icon="material-symbols-light:delete-outline"
                                tooltip="Quitar"
                                aria-label={`Quitar ${line.name}`}
                                onClick={() => removeFromCart(empresa(), line.productId, line.variantId)}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                )}
              </For>
            </div>

            {/* Resumen */}
            <div class="lg:sticky lg:top-4">
              <Card>
                <Card.Header>
                  <h2 class="text-lg font-semibold text-foreground">Resumen</h2>
                </Card.Header>
                <Card.Body>
                  <dl class="space-y-2 text-sm">
                    <div class="flex justify-between">
                      <dt class="text-foreground-subtle">Subtotal</dt>
                      <dd class="font-medium">{money(cartSubtotal(empresa()))}</dd>
                    </div>
                    <div class="flex justify-between text-foreground-subtle">
                      <dt>Impuestos</dt>
                      <dd>Se calculan al pagar</dd>
                    </div>
                  </dl>

                  <Button fullWidth size="lg" class="mt-4" prependIcon="material-symbols-light:lock-outline" onClick={() => navigate(`/catalogo/${empresa()}/pago` as Path)}>
                    Continuar al pago
                  </Button>

                  <p class="mt-3 text-center text-xs text-foreground-subtle">{currency()} · El cobro lo procesa la pasarela de pago.</p>
                </Card.Body>
              </Card>
            </div>
          </div>
        </Show>
      </Container>
    </>
  )
}

export default CarritoPage
